import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

/* ------------------------- CONSTANTS & HELPERS ----------------------- */
const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&family=Roboto:wght@300;400;500;700&display=swap');
`;

const LS_KEY = "hrms_sla_rules_v2";

const uid = (p = "id") => `${p}_${Date.now().toString(36)}_${Math.floor(Math.random() * 9999)}`;

const SLA_MODULES = ["Recruitment", "Onboarding", "Tickets", "Payroll", "Attendance", "Performance"];

const DEFAULT_RULES = [
  {
    id: uid("sla"),
    module: "Tickets",
    name: "General Support SLA",
    target: 24,
    unit: "Hours",
    severity: "Medium",
    description: "Standard SLA for employee support tickets, covering response and resolution times for general inquiries.",
    escalations: [
      { after: 24, level: "HR Manager" },
      { after: 48, level: "HR Director" }
    ]
  }
];

/* ------------------------- Modal Component ----------------------- */
const Modal = ({ open, title, onClose, width = "max-w-2xl", children }) => {
  if (!open) return null;
  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center pt-20 px-4"
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          className={`bg-white rounded-2xl shadow-2xl w-full ${width} max-h-[70vh] overflow-auto`}
        >
          <div className="px-5 py-4 border-b sticky top-0 bg-white z-10 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            <button onClick={onClose} className="px-3 py-1 rounded hover:bg-slate-100 text-slate-600">✕</button>
          </div>
          <div className="p-5">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

/* ---------------------- Main Component ---------------------- */
export default function SLAManagement() {
  
  // Initialize rules using the correctly positioned DEFAULT_RULES
  const [rules, setRules] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      // Use DEFAULT_RULES if localStorage is empty or parsing fails
      if (!raw) return DEFAULT_RULES; 
      const parsed = JSON.parse(raw);
      // Ensure the structure is an array before returning
      return Array.isArray(parsed) ? parsed : DEFAULT_RULES;
    } catch {
      console.error("Failed to load SLA rules from local storage. Using defaults.");
      return DEFAULT_RULES;
    }
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [toast, setToast] = useState("");

  // Persist rules to local storage whenever they change
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(rules));
  }, [rules]);

  /* ------------------ CRUD ------------------ */
  const openCreate = () => {
    setEditingRule({
      id: uid("sla"),
      module: "",
      name: "",
      target: 24,
      unit: "Hours",
      severity: "Medium",
      description: "",
      escalations: []
    });
    setIsModalOpen(true);
  };

  const openEdit = (rule) => {
    setEditingRule({ ...rule });
    setIsModalOpen(true);
  };

  const saveRule = (rule) => {
    setRules((prev) => {
      const idx = prev.findIndex((x) => x.id === rule.id);
      if (idx === -1) return [rule, ...prev]; // Add new
      const cp = [...prev];
      cp[idx] = rule; // Update existing
      return cp;
    });
    setIsModalOpen(false);
    showToast("SLA Rule Saved");
  };

  const deleteRule = (id) => {
    if (!window.confirm("Are you sure you want to delete this SLA rule?")) return;
    setRules((p) => p.filter((r) => r.id !== id));
    showToast("SLA Rule Deleted");
  };

  /* ------------------ Export/Import ------------------ */
  const exportRules = () => {
    const blob = new Blob([JSON.stringify(rules, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "sla_rules.json";
    a.click();
    showToast("Rules Exported");
  };

  const importRules = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        if (!Array.isArray(parsed)) throw new Error("File content is not an array.");
        
        const normalized = parsed.map((it) => ({ 
            ...it, 
            id: it.id || uid("sla"), // Ensure every imported rule has an ID
            escalations: it.escalations || [] // Ensure escalations array exists
        }));
        
        // Merge imported rules with existing ones
        setRules((p) => {
            const existingIds = new Set(p.map(r => r.id));
            const newRules = normalized.filter(r => !existingIds.has(r.id));
            return [...newRules, ...p];
        });

        showToast("Successfully Imported SLA Rules");
      } catch (error) {
        alert("Invalid file format. Please ensure the file contains valid JSON array of SLA rules.");
        console.error("Import error:", error);
      }
    };
    reader.readAsText(file);
  };

  /* ------------------ Toast ------------------ */
  const showToast = (msg) => {
    setToast(msg);
    // Clear toast after 1.5 seconds
    setTimeout(() => setToast(""), 1500); 
  };

  /* ------------------ UI ------------------ */
  return (
    <div className="min-h-screen bg-slate-50 p-6" style={{ fontFamily: "Poppins, Roboto, sans-serif" }}>
      <style>{FONT_IMPORT}</style>

      {/* Toast Notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="fixed top-6 right-6 bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-xl z-[100] text-sm font-medium"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">SLA Management</h1>
            <p className="text-sm text-slate-500">Define response & resolution SLAs for HR workflows.</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button onClick={openCreate} className="bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-md hover:bg-indigo-700 text-sm transition-colors">
              + Create SLA
            </button>

            <button onClick={exportRules} className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm hover:bg-slate-100 transition-colors">Export</button>

            <label className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm cursor-pointer hover:bg-slate-100 transition-colors">
              Import
              <input type="file" accept=".json" className="hidden" onChange={(e) => e.target.files[0] && importRules(e.target.files[0])} />
            </label>

            <button onClick={() => setRules(DEFAULT_RULES)} className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm hover:bg-slate-100 transition-colors">Reset</button>
          </div>
        </header>

        {/* SLA List */}
        <section className="bg-white rounded-2xl shadow-lg p-5">
          <h3 className="text-xl font-semibold text-slate-800 mb-4 border-b pb-2">All SLA Rules ({rules.length})</h3>

          <div className="space-y-4">
            {rules.length === 0 && (
              <div className="text-center text-slate-500 py-10 border border-dashed rounded-xl">
                <p>No SLA rules have been defined yet.</p>
                <button onClick={openCreate} className="mt-3 text-indigo-600 hover:text-indigo-800 font-medium text-sm">Click here to create the first one.</button>
              </div>
            )}

            {rules.map((r) => (
              <div key={r.id} className="p-4 border border-slate-200 rounded-xl bg-white shadow-sm hover:shadow-md transition flex justify-between items-start">
                <div className="flex-1 min-w-0 pr-4">
                  <div className="text-lg font-semibold text-slate-900">{r.name}</div>

                  <div className="text-sm text-slate-600 mt-1 flex flex-wrap gap-x-4">
                    <span className="font-medium text-indigo-600">{r.module}</span> 
                    &bull; 
                    <span className="font-medium">Target: {r.target} {r.unit}</span>
                    &bull;
                    <span className={`font-semibold ${r.severity === 'Critical' ? 'text-red-600' : r.severity === 'High' ? 'text-orange-500' : 'text-green-600'}`}>
                        {r.severity}
                    </span>
                  </div>

                  <div className="text-xs text-slate-500 mt-2 line-clamp-2">{r.description}</div>

                  {/* Escalation */}
                  <div className="mt-3 pt-2 border-t border-slate-100">
                    <h5 className="text-xs font-semibold text-slate-700">Escalations:</h5>
                    {r.escalations.length === 0 && (
                      <div className="text-xs text-slate-500 ml-1">None defined.</div>
                    )}

                    {r.escalations.map((e, idx) => (
                      <div key={idx} className="text-xs text-slate-600 ml-1">
                        After <span className="font-bold text-slate-800">{e.after} {r.unit}</span> → Notify <span className="font-medium text-red-500">{e.level}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <button onClick={() => openEdit(r)} className="text-sm px-4 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors shadow-sm">
                    Edit
                  </button>
                  <button onClick={() => deleteRule(r.id)} className="text-sm px-4 py-1.5 rounded-lg bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-colors shadow-sm">
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Modal */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingRule?.id ? (editingRule.name || "Edit SLA Rule") : "Create New SLA Rule"} width="max-w-xl">
        {editingRule && (
          <SLAForm
            initial={editingRule}
            onSave={saveRule}
            onCancel={() => setIsModalOpen(false)}
          />
        )}
      </Modal>
    </div>
  );
}

/* ---------------------- SLA FORM COMPONENT --------------------- */
function SLAForm({ initial, onSave, onCancel }) {
  const [rule, setRule] = useState(initial);

  const update = (patch) => setRule((r) => ({ ...r, ...patch }));

  const updateEsc = (idx, patch) => {
    const arr = [...rule.escalations];
    arr[idx] = { ...arr[idx], ...patch };
    update({ escalations: arr });
  };

  const addEscalation = () => {
    // Suggest the next escalation to be 1.5 times the current target if possible
    const nextAfter = rule.target ? Math.ceil(rule.target * 1.5) : 24;
    update({ escalations: [...rule.escalations, { after: nextAfter, level: "HR Manager" }] });
  }

  const handleSave = () => {
    if (!rule.name || !rule.module || !rule.target || rule.target <= 0) {
        alert("Please ensure the SLA Name, Module, and a valid Target are set.");
        return;
    }
    onSave(rule);
  }

  return (
    <div className="space-y-6">
      {/* Basic Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-xs text-slate-600 font-medium">Module <span className="text-red-500">*</span></label>
          <select 
            value={rule.module} 
            onChange={(e) => update({ module: e.target.value })} 
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-700 focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
          >
            <option value="">Select Module</option>
            {SLA_MODULES.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-slate-600 font-medium">Rule Name <span className="text-red-500">*</span></label>
          <input 
            value={rule.name} 
            onChange={(e) => update({ name: e.target.value })} 
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-700 focus:border-indigo-500 focus:ring-indigo-500 transition-colors" 
            placeholder="e.g., Critical Ticket Resolution" 
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs text-slate-600 font-medium">Target Time <span className="text-red-500">*</span></label>
          <input 
            type="number" 
            min="1"
            value={rule.target} 
            onChange={(e) => update({ target: Number(e.target.value) })} 
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-700 focus:border-indigo-500 focus:ring-indigo-500 transition-colors" 
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs text-slate-600 font-medium">Unit <span className="text-red-500">*</span></label>
          <select 
            value={rule.unit} 
            onChange={(e) => update({ unit: e.target.value })} 
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-700 focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
          >
            <option>Hours</option>
            <option>Days</option>
            <option>Business Days</option>
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-slate-600 font-medium">Severity</label>
          <select 
            value={rule.severity} 
            onChange={(e) => update({ severity: e.target.value })} 
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-700 focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
          >
            <option>Low</option>
            <option>Medium</option>
            <option>High</option>
            <option>Critical</option>
          </select>
        </div>
      </div>

      {/* Description */}
      <div className="space-y-1">
        <label className="text-xs text-slate-600 font-medium">Description</label>
        <textarea 
          value={rule.description} 
          onChange={(e) => update({ description: e.target.value })} 
          className="w-full px-3 py-2 border border-slate-300 rounded-lg h-20 text-slate-700 focus:border-indigo-500 focus:ring-indigo-500 transition-colors resize-none" 
          placeholder="Briefly describe the purpose and scope of this SLA rule."
        />
      </div>

      {/* Escalations */}
      <div className="border border-indigo-200 bg-indigo-50 p-4 rounded-xl">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-indigo-800">Escalation Levels</h4>
          <button
            type="button"
            onClick={addEscalation}
            className="px-3 py-1 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-md"
          >
            + Add Step
          </button>
        </div>

        <div className="space-y-3">
          {rule.escalations.length === 0 && (
            <div className="text-xs text-indigo-600 py-2 text-center border border-dashed border-indigo-300 rounded-lg">
                No escalation steps defined.
            </div>
          )}

          {rule.escalations.map((e, idx) => (
            <div key={idx} className="grid grid-cols-7 gap-3 items-center bg-white p-3 rounded-lg border">
              <div className="col-span-3">
                <label className="text-xs text-slate-500 block mb-0.5">After ({rule.unit})</label>
                <input
                  type="number"
                  min="1"
                  value={e.after}
                  onChange={(ev) => updateEsc(idx, { after: Number(ev.target.value) })}
                  className="w-full px-2 py-1 border rounded-md text-sm"
                />
              </div>
              <div className="col-span-3">
                <label className="text-xs text-slate-500 block mb-0.5">Notify Role/Person</label>
                <input
                  value={e.level}
                  onChange={(ev) => updateEsc(idx, { level: ev.target.value })}
                  className="w-full px-2 py-1 border rounded-md text-sm"
                  placeholder="e.g., VP of Operations"
                />
              </div>
              <button
                type="button"
                onClick={() => update({ escalations: rule.escalations.filter((_, i) => i !== idx) })}
                className="col-span-1 px-1 py-1.5 mt-4 bg-rose-100 text-rose-600 rounded-md text-xs font-medium hover:bg-rose-200 transition-colors"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
        <button type="button" onClick={onCancel} className="px-5 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors">Cancel</button>
        <button type="button" onClick={handleSave} className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-md">
            {initial.id === rule.id ? "Save Changes" : "Create Rule"}
        </button>
      </div>
    </div>
  );
}